// DS #26 drawn as his playbook's rubric (DC-245, B14 C6): "Scoring summary & rubric" — the scoring summary line, the
// average core score box "x.xx / 5" (read from the server, never computed here; "from n of 5 values" when fewer than
// five values were scored; "—" when there is none), then the rubric lines verbatim, each with its dot, the band the
// average falls in highlighted only when the server says so (`band.highlight`). The rubric suggests; it never chooses.

import { cx } from "../lib/cx";
import { useSvgId } from "../lib/useSvgId";
import { RECOMMENDATION_LABELS, RECOMMENDATION_TONES, type RecoTone, type RecommendationValue } from "../labels";
import { PLAYBOOK_TEMPLATES } from "./labels";

export interface PlaybookRubricBand {
  key: string;
  text: string;
  highlight: boolean;
}

export interface PlaybookRubricProps {
  /** The server's average core score, 2 dp ("4.50"), or null when no value is scored. */
  average: string | null;
  valuesScored: number;
  /** The values the average is taken over (5). */
  ofValues?: number | undefined;
  /** The pinned guide's rubric lines, verbatim. */
  lines: readonly string[];
  /** The band the average falls in (C9), or null. */
  band: PlaybookRubricBand | null;
  /** The block's heading ("Scoring summary & rubric"). */
  heading: string;
  /** The guide's scoring summary line. */
  scoringSummaryText: string;
  /** The guide's bands note ("A guide, not a rule …"). */
  note?: string | undefined;
  className?: string | undefined;
}

/** The recommendation a rubric line names, read from its words after the colon (his legend: "3.5+ : Hire"). */
const LINE_WORD: ReadonlyArray<readonly [RegExp, RecommendationValue]> = [
  [new RegExp(`:\\s*${RECOMMENDATION_LABELS.strongHire}\\s*$`), "strong_hire"],
  [new RegExp(`:\\s*${RECOMMENDATION_LABELS.noHire}\\s*$`), "no_hire"],
  [/:\s*Hire w\/ Reservations\s*$/, "hire_with_reservations"],
  [new RegExp(`:\\s*${RECOMMENDATION_LABELS.hire}\\s*$`), "hire"],
  [/:\s*Caution\s*\/\s*Consider\s*$/, "consider_other_role"],
];

/** A line's dot tone: the band's own when the line is the band's, else the recommendation it names, else neutral. */
export function rubricLineTone(line: string, band: PlaybookRubricBand | null): RecoTone | "none" {
  if (band && band.text === line && band.key in RECOMMENDATION_TONES) return RECOMMENDATION_TONES[band.key as RecommendationValue];
  const hit = LINE_WORD.find(([pattern]) => pattern.test(line));
  return hit ? RECOMMENDATION_TONES[hit[1]] : "none";
}

export function PlaybookRubric({ average, valuesScored, ofValues = 5, lines, band, heading, scoringSummaryText, note, className }: PlaybookRubricProps) {
  const id = useSvgId();
  const headingId = `${id}-rubric`;
  const fewer = average !== null && valuesScored < ofValues;
  return (
    <section className={cx("mc-rubric", className)} aria-labelledby={headingId}>
      <h3 className="mc-rubric-t" id={headingId}>{heading}</h3>
      <p className="mc-rubric-s" id={`${id}-sum`}>{scoringSummaryText}</p>
      <div className="mc-rubric-avg">
        <output className={cx("mc-rubric-box", average === null && "mc-rubric-none")} aria-labelledby={`${id}-sum`}>
          {average === null ? "—" : PLAYBOOK_TEMPLATES.outOfFive({ average })}
        </output>
        {fewer ? <span className="mc-rubric-of">{PLAYBOOK_TEMPLATES.fromValues({ n: valuesScored })}</span> : null}
      </div>
      <ul className="mc-rubric-l">
        {lines.map((line) => {
          const on = Boolean(band?.highlight && band.text === line);
          const tone = rubricLineTone(line, band);
          return (
            <li key={line} className={cx("mc-rubric-i", on && "mc-on")} aria-current={on ? "true" : undefined}>
              <i className={cx("mc-rubric-dot", tone !== "none" && `mc-rt-${tone}`)} aria-hidden="true" />
              {line}
            </li>
          );
        })}
      </ul>
      {note ? <p className="mc-rubric-n">{note}</p> : null}
    </section>
  );
}
